import React, { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { ShoppingCart, Trash2, Plus, Minus, ChevronLeft, CreditCard, Wallet, AlertCircle, Info } from 'lucide-react'
import { useCustomerAuth } from '../../contexts/CustomerAuthContext'
import { apiService } from '../../services/apiService'

const KataleCart = () => {
  const navigate = useNavigate()
  const { customer } = useCustomerAuth()
  const [cartItems, setCartItems] = useState([])
  const [loading, setLoading] = useState(true)
  const [accountBalance, setAccountBalance] = useState({ 
    total: 0, 
    available: 0,
    voluntary: 0, 
    programmed: 0,
    accounts: []
  })
  const [deliveryAddress, setDeliveryAddress] = useState('')
  const [processingCheckout, setProcessingCheckout] = useState(false)
  const [error, setError] = useState('')

  // Commission rate (5% to UFCC)
  const COMMISSION_RATE = 0.05

  useEffect(() => {
    if (customer) {
      fetchCartData()
    } else {
      setLoading(false)
    }
  }, [customer])

  // Helper to extract MG code from tenant DB (e.g., kololo_mg from mgfina6_tenant_kololo_mg)
  const getMgCode = (tenantDb) => {
    if (!tenantDb) return null
    const prefix = 'mgfina6_tenant_'
    if (tenantDb.startsWith(prefix)) {
      return tenantDb.substring(prefix.length)
    }
    const parts = tenantDb.split('_')
    return parts.length > 1 ? parts[1] : tenantDb
  }

  const fetchCartData = async () => {
    setLoading(true)
    setError('')
    
    const buyerMemNo = customer?.memberNo
    const buyerMgCode = getMgCode(customer?.tenantDb)
    
    if (!buyerMemNo || !buyerMgCode) {
      setLoading(false)
      return
    }
    
    try {
      // Fetch cart and balance in parallel
      const [cartRes, balanceRes] = await Promise.all([
        apiService.getKataleCart(buyerMemNo, buyerMgCode),
        apiService.getKataleSavingsBalance(buyerMemNo, buyerMgCode)
      ])

      if (cartRes.status === 'SUCCESS' && cartRes.data) {
        setCartItems(cartRes.data || [])
      }

      if (balanceRes.status === 'SUCCESS' && balanceRes.data) {
        setAccountBalance({ 
          total: balanceRes.data.total_balance || 0,
          available: balanceRes.data.available_balance || 0,
          voluntary: balanceRes.data.voluntary_balance || 0,
          programmed: balanceRes.data.programmed_balance || 0,
          accounts: balanceRes.data.accounts || []
        })
      }
    } catch (err) {
      console.error('Failed to fetch cart data:', err)
      setError('Failed to load cart. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-UG', {
      style: 'currency',
      currency: 'UGX',
      minimumFractionDigits: 0
    }).format(amount)
  }

  const updateQuantity = async (cartItemId, newQuantity) => {
    if (newQuantity < 1) return
    
    // Optimistic update
    setCartItems(items =>
      items.map(item =>
        item.id === cartItemId ? { ...item, quantity: newQuantity } : item
      )
    )
    
    try {
      const response = await apiService.updateKataleCartItem(cartItemId, newQuantity)
      if (response.status !== 'SUCCESS') {
        // Revert on failure
        fetchCartData()
      }
    } catch (err) {
      console.error('Failed to update cart:', err)
      fetchCartData()
    }
  }

  const removeItem = async (cartItemId) => {
    // Optimistic update
    setCartItems(items => items.filter(item => item.id !== cartItemId))
    
    try {
      const response = await apiService.removeFromKataleCart(cartItemId)
      if (response.status !== 'SUCCESS') {
        fetchCartData()
      }
    } catch (err) {
      console.error('Failed to remove item:', err)
      fetchCartData()
    }
  }

  const subtotal = cartItems.reduce((sum, item) => sum + (item.price * item.quantity), 0)
  const deliveryFee = cartItems.length > 0 ? 10000 : 0 // Flat delivery fee
  const ufccCommission = Math.round(subtotal * COMMISSION_RATE) // 5% commission to UFCC
  const total = subtotal + deliveryFee + ufccCommission

  const hasEnoughBalance = accountBalance.voluntary >= total

  const handleCheckout = async () => {
    if (accountBalance.voluntary <= 0) {
      alert('You have no balance on your voluntary savings account. Payments can only be made from your voluntary savings account.')
      return
    }
    if (!hasEnoughBalance) {
      alert('Insufficient voluntary savings balance. Your voluntary savings balance is ' + formatCurrency(accountBalance.voluntary) + ' but you need ' + formatCurrency(total) + '. Payments can only be made from your voluntary savings account.')
      return
    }
    if (!deliveryAddress.trim()) {
      alert('Please enter a delivery address.')
      return
    }

    const buyerMemNo = customer?.memberNo
    const buyerMgCode = getMgCode(customer?.tenantDb)

    setProcessingCheckout(true)
    
    try {
      const response = await apiService.kataleCheckout(
        buyerMemNo,
        buyerMgCode,
        deliveryAddress,
        deliveryFee
      )
      
      if (response.status === 'SUCCESS') {
        alert('Order placed successfully!')
        navigate('/katale/orders')
      } else {
        alert(response.message || 'Failed to place order. Please try again.')
      }
    } catch (err) {
      console.error('Checkout error:', err)
      alert('Failed to place order. Please try again.')
    } finally {
      setProcessingCheckout(false)
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center">
            <Link to="/katale" className="text-gray-600 hover:text-gray-900 mr-4">
              <ChevronLeft className="h-5 w-5" />
            </Link>
            <ShoppingCart className="h-6 w-6 text-blue-600 mr-2" />
            <h1 className="text-2xl font-bold text-gray-900">Shopping Cart</h1>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {cartItems.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm p-12 text-center">
            <ShoppingCart className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Your cart is empty</h2>
            <p className="text-gray-600 mb-6">Browse our products and add items to your cart.</p>
            <Link to="/katale/products" className="btn btn-primary">
              Browse Products
            </Link>
          </div>
        ) : (
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Cart Items */}
            <div className="lg:col-span-2 space-y-4">
              {cartItems.map((item) => (
                <div key={item.id} className="bg-white rounded-lg shadow-sm p-4">
                  <div className="flex items-start space-x-4">
                    <div className="w-20 h-20 bg-gray-200 rounded-lg flex items-center justify-center flex-shrink-0 overflow-hidden">
                      {item.primary_image || item.image_url ? (
                        <img src={item.primary_image || item.image_url} alt={item.name} className="w-full h-full object-cover" />
                      ) : (
                        <span className="text-gray-400 text-xs">No Image</span>
                      )}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-start justify-between">
                        <div>
                          <h3 className="font-semibold text-gray-900">{item.name}</h3>
                          <p className="text-sm text-gray-500">by {item.seller} • {item.sellerMg}</p>
                          <p className="text-lg font-bold text-blue-600 mt-1">{formatCurrency(item.price)}</p>
                        </div>
                        <button
                          onClick={() => removeItem(item.id)}
                          className="text-red-500 hover:text-red-700 p-1"
                        >
                          <Trash2 className="h-5 w-5" />
                        </button>
                      </div>
                      <div className="flex items-center justify-between mt-3">
                        <div className="flex items-center space-x-3">
                          <button
                            onClick={() => updateQuantity(item.id, item.quantity - 1)}
                            className="p-1 rounded-full border border-gray-300 hover:bg-gray-100"
                          >
                            <Minus className="h-4 w-4" />
                          </button>
                          <span className="font-medium w-8 text-center">{item.quantity}</span>
                          <button
                            onClick={() => updateQuantity(item.id, item.quantity + 1)}
                            disabled={item.quantity >= item.stock}
                            className="p-1 rounded-full border border-gray-300 hover:bg-gray-100 disabled:opacity-50"
                          >
                            <Plus className="h-4 w-4" />
                          </button>
                          <span className="text-xs text-gray-400">({item.stock} available)</span>
                        </div>
                        <span className="font-bold text-gray-900">
                          {formatCurrency(item.price * item.quantity)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Order Summary */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-lg shadow-sm p-6 sticky top-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Order Summary</h2>

                <div className="space-y-3 mb-6">
                  <div className="flex justify-between text-gray-600">
                    <span>Subtotal ({cartItems.length} items)</span>
                    <span>{formatCurrency(subtotal)}</span>
                  </div>
                  <div className="flex justify-between text-gray-600">
                    <span>Delivery Fee</span>
                    <span>{formatCurrency(deliveryFee)}</span>
                  </div>
                  <div className="flex justify-between text-gray-600">
                    <div className="flex items-center">
                      <span>UFCC Commission (5%)</span>
                      <div className="relative group ml-1">
                        <Info className="h-4 w-4 text-gray-400 cursor-help" />
                        <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity w-48 pointer-events-none z-10">
                          5% commission on all transactions goes to UFCC to support the platform and cross-MG settlements.
                        </div>
                      </div>
                    </div>
                    <span>{formatCurrency(ufccCommission)}</span>
                  </div>
                  <hr />
                  <div className="flex justify-between text-lg font-bold text-gray-900">
                    <span>Total</span>
                    <span>{formatCurrency(total)}</span>
                  </div>
                </div>

                {/* Payment Method - Savings Account */}
                <div className="mb-6">
                  <h3 className="font-medium text-gray-900 mb-3">Payment Method</h3>
                  <div className="p-4 border-2 border-blue-500 bg-blue-50 rounded-lg">
                    <div className="flex items-center mb-3">
                      <CreditCard className="h-6 w-6 text-blue-600 mr-3" />
                      <div className="flex-1">
                        <span className="font-medium text-gray-900">Savings Account</span>
                        <p className="text-sm font-semibold text-blue-600">Available: {formatCurrency(accountBalance.available)}</p>
                      </div>
                    </div>
                    {/* Breakdown of savings types */}
                    <div className="border-t border-blue-200 pt-3 space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600 flex items-center">
                          <Wallet className="h-4 w-4 mr-2 text-green-500" />
                          Voluntary Savings
                        </span>
                        <span className="font-medium text-green-600">{formatCurrency(accountBalance.voluntary)}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600 flex items-center">
                          <Wallet className="h-4 w-4 mr-2 text-purple-500" />
                          Programmed Savings
                        </span>
                        <span className="font-medium text-purple-600">{formatCurrency(accountBalance.programmed)}</span>
                      </div>
                      <div className="flex justify-between text-sm border-t border-blue-200 pt-2">
                        <span className="text-gray-500 text-xs">Balance</span>
                        <span className="text-gray-500 text-xs">{formatCurrency(accountBalance.total)}</span>
                      </div>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    Payment will be deducted from your <strong>voluntary savings</strong> account only.
                  </p>
                </div>

                {/* Insufficient Balance Warning */}
                {accountBalance.voluntary <= 0 && (
                  <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start">
                    <AlertCircle className="h-5 w-5 text-red-500 mr-2 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-red-700">
                      <p className="font-medium">No Voluntary Savings Balance</p>
                      <p>You have no balance on your voluntary savings account. Payments can only be made from your voluntary savings account. Please deposit funds first.</p>
                    </div>
                  </div>
                )}
                {accountBalance.voluntary > 0 && !hasEnoughBalance && (
                  <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start">
                    <AlertCircle className="h-5 w-5 text-red-500 mr-2 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-red-700">
                      <p className="font-medium">Insufficient Voluntary Savings Balance</p>
                      <p>You need {formatCurrency(total - accountBalance.voluntary)} more in your voluntary savings to complete this purchase.</p>
                    </div>
                  </div>
                )}

                {/* Delivery Address */}
                <div className="mb-6">
                  <h3 className="font-medium text-gray-900 mb-3">Delivery Address</h3>
                  <textarea
                    value={deliveryAddress}
                    onChange={(e) => setDeliveryAddress(e.target.value)}
                    placeholder="Enter your delivery address..."
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    rows={3}
                  />
                </div>

                {/* Checkout Button */}
                <button
                  onClick={handleCheckout}
                  disabled={!hasEnoughBalance || processingCheckout || !deliveryAddress.trim()}
                  className="w-full btn btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {processingCheckout ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                      Processing...
                    </>
                  ) : (
                    <>Place Order</>
                  )}
                </button>

                <p className="text-xs text-gray-500 text-center mt-4">
                  By placing this order, you agree to pay {formatCurrency(total)} from your voluntary savings account.
                  This includes a 5% UFCC commission ({formatCurrency(ufccCommission)}).
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default KataleCart
